import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentMultiDocumentVerificationSubmitInputV2,
  type EnrollmentMultiDocumentVerificationDataV2,
  type EnrollmentMultiDocumentVerificationSubmitOutputV2,
  type EnrollmentMultiDocVerificationCheckStatusInputV2,
} from '../../../../models/enrollment/multi/document-verification_v2'

/**
 * Get Enrollment Multi DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationData>
 * @example const response = await getEnrollmentMultiDocumentVerificationData()
 */
export const getEnrollmentMultiDocumentVerificationDataV2 = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationDataV2> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiDocumentVerificationData',
      query: `mutation getEnrollmentMultiDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationDataV2 }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiDocumentVerificationDataV2 = createQueryHook<
  undefined,
  EnrollmentMultiDocumentVerificationDataV2
>(getEnrollmentMultiDocumentVerificationDataV2)

/**
 * Submit Enrollment Multi DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentMultiDocumentVerificationData();
 */
export const submitEnrollmentMultiDocumentVerificationDataV2 = async (
  enrollmentDocumentVerificationSubmitInput: EnrollmentMultiDocumentVerificationSubmitInputV2,
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationSubmitOutputV2> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDocumentVerificationData',
      query: `mutation submitEnrollmentMultiDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationStatus
              documentVerificationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocumentVerificationSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationSubmitOutputV2 }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDocumentVerificationDataV2 = createMutationHook<
  EnrollmentMultiDocumentVerificationSubmitInputV2,
  EnrollmentMultiDocumentVerificationSubmitOutputV2
>(submitEnrollmentMultiDocumentVerificationDataV2)

/**
 * Submit Enrollment Multi DocumentVerificationCheckStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentMultiDocVerificationCheckStatusData();
 */
export const submitEnrollmentMultiDocVerificationCheckStatusDataV2 = async (
  enrollmentDocVerificationCheckStatusInput: EnrollmentMultiDocVerificationCheckStatusInputV2,
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationSubmitOutputV2> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDocVerificationCheckStatusData',
      query: `mutation submitEnrollmentMultiDocVerificationCheckStatusData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationStatus
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocVerificationCheckStatusInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationSubmitOutputV2 }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDocVerificationCheckStatusDataV2 = createMutationHook<
  EnrollmentMultiDocVerificationCheckStatusInputV2,
  EnrollmentMultiDocumentVerificationSubmitOutputV2
>(submitEnrollmentMultiDocVerificationCheckStatusDataV2)
